import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const BOTTOM_THRESHOLD_PX = 80;
const SMOOTH_SCROLL_DISTANCE_PX = 100;
const SMOOTH_SCROLL_TIMEOUT_MS = 500;

export type UseStickToBottomResult = {
  scrollRef: React.RefObject<HTMLDivElement>;
  contentRef: React.RefObject<HTMLDivElement>;
  isAtBottom: boolean;
  scrollToBottom: () => void;
};

const scrollToEnd = (
  container: HTMLDivElement,
  { smooth }: { smooth: boolean } = { smooth: false },
) => {
  const distance =
    container.scrollHeight - container.scrollTop - container.clientHeight;
  container.scrollTo({
    top: container.scrollHeight,
    behavior:
      smooth && distance > SMOOTH_SCROLL_DISTANCE_PX ? 'smooth' : 'auto',
  });
};

export const useStickToBottom = (): UseStickToBottomResult => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const smoothScrollingRef = useRef(false);
  const smoothScrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [isAtBottom, setIsAtBottom] = useState(true);

  const updateBottomState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const scrollableDistance = container.scrollHeight - container.clientHeight;
    const effectiveThreshold = Math.max(
      1,
      Math.min(BOTTOM_THRESHOLD_PX, scrollableDistance / 2),
    );
    const nextIsAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      effectiveThreshold;

    if (nextIsAtBottom !== isAtBottomRef.current) {
      isAtBottomRef.current = nextIsAtBottom;
      startTransition(() => setIsAtBottom(nextIsAtBottom));
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    isAtBottomRef.current = true;
    setIsAtBottom(true);
    smoothScrollingRef.current = true;
    clearTimeout(smoothScrollTimeoutRef.current);
    scrollToEnd(container, { smooth: true });
    smoothScrollTimeoutRef.current = setTimeout(() => {
      smoothScrollingRef.current = false;
    }, SMOOTH_SCROLL_TIMEOUT_MS);
  }, []);

  // Pin scroll to bottom before every paint while stuck to bottom.
  // This catches the initial page-entry case where cached events
  // render synchronously and the useEffect-based scroll fires too
  // late (after updateBottomState has already set isAtBottomRef to
  // false).
  useLayoutEffect(() => {
    if (!isAtBottomRef.current || smoothScrollingRef.current) return;
    const container = scrollRef.current;
    if (!container) return;
    if (
      container.scrollHeight - container.scrollTop - container.clientHeight >
      1
    ) {
      container.scrollTop = container.scrollHeight - container.clientHeight;
    }
  });

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => updateBottomState();
    container.addEventListener('scroll', handleScroll, { passive: true });
    updateBottomState();

    return () => container.removeEventListener('scroll', handleScroll);
  }, [updateBottomState]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    let rafId = 0;

    const observer = new ResizeObserver(() => {
      if (!isAtBottomRef.current) {
        return;
      }

      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const container = scrollRef.current;
        if (container && isAtBottomRef.current) {
          scrollToEnd(container);
        }
      });
    });

    observer.observe(content);
    if (isAtBottomRef.current) {
      const container = scrollRef.current;
      if (container) {
        scrollToEnd(container);
      }
    }

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  return {
    scrollRef,
    contentRef,
    isAtBottom,
    scrollToBottom,
  };
};
